import * as React from 'react';
import {useState, useRef} from 'react';
import Button from '@mui/material/Button';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Popper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    createOrUpdateHomeMatchAd,
    deleteHomeMatchAd,
    DestinationFolder,
    getHomeMatchAd,
    getMatchById,
    getLeagueById,
    getSearchMatches,
    getSearchLeagues,
    uploadFile,
} from '../Api';
import {useNavigate, useParams} from 'react-router-dom';
import HomeMatchAdDto, {HomeMatchAdLinkType} from "../models/dtos/HomeMatchAdDto";
import MatchDto from "../models/dtos/MatchDto";
import LeagueInfoDto from "../models/dtos/LeagueInfoDto";

const formatMatchDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

function isProbablyValidResourceUrl(s: string): boolean {
    const t = s.trim();
    if (!t) return false;
    if (/^https?:\/\//i.test(t) || t.startsWith('//')) {
        try {
            new URL(t.startsWith('//') ? `https:${t}` : t);
            return true;
        } catch {
            return false;
        }
    }
    return true;
}

function validateHomeMatchAdForm(homeMatchAd: HomeMatchAdDto): Record<string, string> {
    const e: Record<string, string> = {};
    if (!homeMatchAd.name?.trim()) {
        e.name = 'Title / name is required — it appears on the card in the app.';
    }
    const img = homeMatchAd.imageUrl?.trim() ?? '';
    if (!img) {
        e.imageUrl = 'Add an image URL or upload a file.';
    } else if (!isProbablyValidResourceUrl(img)) {
        e.imageUrl = 'Use a full URL (https://…) or upload an image.';
    }
    if (homeMatchAd.linkType === HomeMatchAdLinkType.MATCH || homeMatchAd.linkType === HomeMatchAdLinkType.LEAGUE) {
        if (homeMatchAd.linkId == null) {
            e.link = 'Pick a match or league, or set link type to None.';
        }
    }
    return e;
}

export default function AddHomeMatchAdPage() {
    let homeMatchAdId = useParams()['id'];

    const [state, setState] = React.useState({
        homeMatchAd: {} as HomeMatchAdDto,
        uploading: false,
    });
    const [pageReady, setPageReady] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [uploadError, setUploadError] = React.useState<string | null>(null);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);

    const navigate = useNavigate();
    let isEditMode = homeMatchAdId != undefined;

    const isExpired = state.homeMatchAd.expirationDate != null && new Date(state.homeMatchAd.expirationDate) < new Date();

    React.useEffect(() => {
        const fetchData = async () => {
            setSubmitError(null);
            setDeleteError(null);
            let homeMatchAd = {} as HomeMatchAdDto;
            if (isEditMode) {
                let item = await getHomeMatchAd(homeMatchAdId!);
                if (item != null) {
                    homeMatchAd = item;
                    if (item.linkId != null) {
                        if (item.linkType === HomeMatchAdLinkType.MATCH) {
                            let match = await getMatchById(item.linkId);
                            if (match) {
                                item.linkName = `[${match.betupId}] ${match.homeTeam?.name} vs ${match.awayTeam?.name} - ${match.sport?.name} - ${formatMatchDate(match.date)}`;
                            } else {
                                item.linkName = item.linkName || `Match #${item.linkId}`;
                            }
                        } else if (item.linkType === HomeMatchAdLinkType.LEAGUE) {
                            let league = await getLeagueById(item.linkId);
                            if (league) {
                                item.linkName = `[${league.id}] ${league.name}${league.sport ? ' - ' + league.sport.name : ''}`;
                            } else {
                                item.linkName = item.linkName || `League #${item.linkId}`;
                            }
                        }
                    }
                } else {
                    navigate('/home-match-ads/add');
                }
            } else {
                homeMatchAd = {
                    id: null,
                    name: "",
                    linkId: null,
                    linkType: HomeMatchAdLinkType.NONE,
                    expirationDate: null,
                    isActive: true,
                    priority: 0,
                    imageUrl: "",
                    linkName: null,
                    createdAt: null,
                    views: 0,
                    clicks: 0,
                };
            }
            setState(prevState => ({
                ...prevState,
                homeMatchAd: homeMatchAd,
            }));
            setPageReady(true);
        };

        fetchData();
    }, []);

    const onCreateClicked = async () => {
        setSubmitError(null);
        const errs = validateHomeMatchAdForm(state.homeMatchAd);
        setFieldErrors(errs);
        if (Object.keys(errs).length > 0) {
            return;
        }
        setSaving(true);
        const result = await createOrUpdateHomeMatchAd(state.homeMatchAd);
        setSaving(false);
        if (result) {
            navigate('/home-match-ads');
        } else {
            const hint = 'Internal server error';
            setSubmitError(hint);
        }
    };

    const onDeleteClicked = async () => {
        setDeleteError(null);
        if (window.confirm('Are you sure you want to delete this ad?')) {
            const success = await deleteHomeMatchAd(homeMatchAdId!);
            if (success) {
                navigate('/home-match-ads');
            } else {
                setDeleteError('Could not delete. Check permissions or try again.');
            }
        }
    };

    const chooseLinkId = (linkId: number, linkName: string) => {
        setFieldErrors(prev => {
            const n = { ...prev };
            delete n.link;
            return n;
        });
        setState(prev => ({
            ...prev, homeMatchAd: {
                ...prev.homeMatchAd, linkId: linkId, linkName: linkName
            },
        }));
    };

    const clearLink = () => {
        setState(prev => ({
            ...prev, homeMatchAd: {
                ...prev.homeMatchAd, linkId: null, linkName: null
            },
        }));
    };

    const onLinkTypeChanged = (newType: number) => {
        setFieldErrors(prev => {
            const n = { ...prev };
            delete n.link;
            return n;
        });
        setState(prev => ({
            ...prev, homeMatchAd: {
                ...prev.homeMatchAd, linkType: newType, linkId: null, linkName: null
            },
        }));
    };

    if (!pageReady) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                    {isEditMode ? 'Edit Home Match Ad' : 'Add New Home Match Ad'}
                </Typography>

                <Accordion defaultExpanded={false} variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>How saving works</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" component="div" sx={{ '& ul': { pl: 2, m: 0 }, mb: 1 }}>
                            <ul>
                                <li><strong>Dashboard (browser)</strong> → <code>POST /api/dashboard/matches/ad</code> with JSON body.</li>
                                <li><strong>Analytics server</strong> stores the ad via <code>HomeAdService</code> (same process as the dashboard API).</li>
                                <li><strong>MongoDB</strong> collection <code>homeMatchAd</code> — one document per ad.</li>
                                <li><strong>Mobile app</strong> loads only <strong>active</strong> ads; past expiration is stored as inactive.</li>
                            </ul>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Required to publish: title (name) + image (URL or upload). Match/league link is optional unless you choose that link type.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                {submitError && (
                    <Alert severity="error" onClose={() => setSubmitError(null)}>
                        {submitError}
                    </Alert>
                )}
                {deleteError && (
                    <Alert severity="error" onClose={() => setDeleteError(null)}>
                        {deleteError}
                    </Alert>
                )}
                {uploadError && (
                    <Alert severity="error" onClose={() => setUploadError(null)}>
                        {uploadError}
                    </Alert>
                )}

                <Typography variant="subtitle1" color="text.secondary">
                    Home match ad details
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Ad id (read-only)"
                            variant="filled"
                            value={state.homeMatchAd.id || ''}
                            fullWidth
                            disabled
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                        <Box sx={{ mt: 2 }}>
                            {state.homeMatchAd.imageUrl ? (
                                <Box
                                    component="img"
                                    src={state.homeMatchAd.imageUrl}
                                    alt="Preview"
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: 220,
                                        objectFit: 'contain',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        display: 'block',
                                        mb: 2,
                                    }}
                                />
                            ) : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                                <TextField
                                    label="Image URL"
                                    variant="filled"
                                    value={state.homeMatchAd.imageUrl || ''}
                                    onChange={e => {
                                        setFieldErrors(prev => {
                                            const n = { ...prev };
                                            delete n.imageUrl;
                                            return n;
                                        });
                                        setState(prev => ({
                                            ...prev, homeMatchAd: {
                                                ...prev.homeMatchAd, imageUrl: e.target.value
                                            },
                                        }));
                                    }}
                                    fullWidth
                                    placeholder="https://… or upload"
                                    error={!!fieldErrors.imageUrl}
                                    helperText={fieldErrors.imageUrl || 'Required — shown as the banner in the app.'}
                                />
                                <Button
                                    variant="contained"
                                    component="label"
                                    disabled={state.uploading}
                                    sx={{ height: '56px', flexShrink: 0, minWidth: { xs: '100%', sm: 120 } }}
                                >
                                    {state.uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = '';
                                            if (file) {
                                                setUploadError(null);
                                                setState(prev => ({ ...prev, uploading: true }));
                                                const url = await uploadFile(file, DestinationFolder.Ads);
                                                if (url) {
                                                    setFieldErrors(prev => {
                                                        const n = { ...prev };
                                                        delete n.imageUrl;
                                                        return n;
                                                    });
                                                    setState(prev => ({
                                                        ...prev,
                                                        uploading: false,
                                                        homeMatchAd: { ...prev.homeMatchAd, imageUrl: url }
                                                    }));
                                                } else {
                                                    setState(prev => ({ ...prev, uploading: false }));
                                                    setUploadError('Upload failed. Check login or try another file.');
                                                }
                                            }
                                        }}
                                    />
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Name (title)"
                            variant="filled"
                            value={state.homeMatchAd.name || ''}
                            onChange={e => {
                                setFieldErrors(prev => {
                                    const n = { ...prev };
                                    delete n.name;
                                    return n;
                                });
                                setState(prev => ({
                                    ...prev, homeMatchAd: {
                                        ...prev.homeMatchAd, name: e.target.value
                                    },
                                }));
                            }}
                            fullWidth
                            required
                            error={!!fieldErrors.name}
                            helperText={fieldErrors.name || 'Shown on the card in dashboard and app.'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Priority"
                            variant="filled"
                            type="number"
                            inputProps={{ step: 1, min: 0 }}
                            value={state.homeMatchAd.priority ?? 0}
                            onChange={e => setState(prev => ({
                                ...prev, homeMatchAd: {
                                    ...prev.homeMatchAd, priority: parseInt(e.target.value, 10) || 0
                                },
                            }))}
                            fullWidth
                            helperText="Higher sorts first among active ads."
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Expiration date"
                            variant="filled"
                            type="datetime-local"
                            value={state.homeMatchAd.expirationDate ? state.homeMatchAd.expirationDate.slice(0, 16) : ''}
                            onChange={e => setState(prev => ({
                                ...prev, homeMatchAd: {
                                    ...prev.homeMatchAd, expirationDate: e.target.value ? new Date(e.target.value).toISOString() : null
                                },
                            }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText="Optional. Past date forces the ad inactive on save."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={state.homeMatchAd.isActive ?? true}
                                    onChange={e => setState(prev => ({
                                        ...prev, homeMatchAd: {
                                            ...prev.homeMatchAd, isActive: e.target.checked
                                        },
                                    }))}
                                    disabled={isExpired}
                                />
                            }
                            label={isExpired ? 'Inactive (expired)' : 'Active'}
                        />
                        {isExpired && (
                            <Typography variant="caption" color="error" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                                Expired ads cannot be reactivated here.
                            </Typography>
                        )}
                    </Grid>
                    {isExpired && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="error" sx={{ display: { xs: 'block', sm: 'none' } }}>
                                Expired ads cannot be reactivated here.
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!fieldErrors.link}>
                            <InputLabel id="link-type-label">Link type</InputLabel>
                            <Select
                                labelId="link-type-label"
                                id="link_type_select"
                                value={Number(state.homeMatchAd.linkType)}
                                onChange={e => onLinkTypeChanged(Number(e.target.value))}
                                label="Link type"
                            >
                                {Object.keys(HomeMatchAdLinkType)
                                    .filter(key => !isNaN(Number(key)))
                                    .map(id => (
                                        <MenuItem key={id} value={Number(id)}>
                                            {HomeMatchAdLinkType[Number(id)]}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {fieldErrors.link ? (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                    {fieldErrors.link}
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                                    For None, tapping the ad uses no deep link. For Match/League, select below.
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {state.homeMatchAd.linkType === HomeMatchAdLinkType.MATCH && (
                        <Grid item xs={12}>
                            <MatchSearchSelect
                                selectedLinkName={state.homeMatchAd.linkName}
                                onChoose={chooseLinkId}
                                onClear={clearLink}
                            />
                        </Grid>
                    )}

                    {state.homeMatchAd.linkType === HomeMatchAdLinkType.LEAGUE && (
                        <Grid item xs={12}>
                            <LeagueSearchSelect
                                selectedLinkName={state.homeMatchAd.linkName}
                                onChoose={chooseLinkId}
                                onClear={clearLink}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={saving}
                                onClick={() => onCreateClicked()}
                                sx={{ minHeight: 48 }}
                            >
                                {saving ? <CircularProgress size={24} color="inherit" /> : `${isEditMode ? 'Save' : 'Create'} ad`}
                            </Button>
                            {isEditMode && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    fullWidth
                                    size="large"
                                    disabled={saving}
                                    onClick={() => onDeleteClicked()}
                                    sx={{ minHeight: 48 }}
                                >
                                    Delete ad
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    );
}

interface SearchSelectProps {
    selectedLinkName: string | null;
    onChoose: (linkId: number, linkName: string) => void;
    onClear: () => void;
}

export function MatchSearchSelect({selectedLinkName, onChoose, onClear}: SearchSelectProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [matchList, setMatchList] = useState<MatchDto[]>([]);
    const [searchText, setSearchText] = useState('');
    const [idSearchText, setIdSearchText] = useState('');
    const [idSearchError, setIdSearchError] = useState<string | null>(null);
    const [idSearching, setIdSearching] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setAnchorEl(event.currentTarget);
        if (!selectedLinkName) {
            setMenuOpen(true);
        }
    };

    const handleInputBlur = () => {
        setTimeout(() => setMenuOpen(false), 200);
    };

    const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchText(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            if (value.length < 2) {
                setMatchList([]);
                return;
            }
            try {
                const list = await getSearchMatches(value);
                setMatchList(list.slice(0, 20));
                setMenuOpen(true);
            } catch (error) {
                console.error('Error fetching matches:', error);
            }
        }, 300);
    };

    const handleMenuItemClick = (match: MatchDto) => {
        const linkName = `[${match.betupId}] ${match.homeTeam?.name} vs ${match.awayTeam?.name} - ${match.sport?.name} - ${formatMatchDate(match.date)}`;
        onChoose(match.betupId, linkName);
        setSearchText('');
        setMenuOpen(false);
        setIdSearchError(null);
    };

    const handleSearchById = async () => {
        const id = parseInt(idSearchText, 10);
        if (isNaN(id)) {
            setIdSearchError('Please enter a valid numeric Match ID');
            return;
        }
        setIdSearching(true);
        setIdSearchError(null);
        try {
            const match = await getMatchById(id);
            if (match) {
                const linkName = `[${match.betupId}] ${match.homeTeam?.name} vs ${match.awayTeam?.name} - ${match.sport?.name} - ${formatMatchDate(match.date)}`;
                onChoose(match.betupId, linkName);
                setIdSearchText('');
            } else {
                setIdSearchError(`Match was not found for ID: ${id}`);
            }
        } catch (error) {
            setIdSearchError(`Match was not found for ID: ${id}`);
        } finally {
            setIdSearching(false);
        }
    };

    const handleClear = () => {
        onClear();
        setSearchText('');
        setMatchList([]);
        setIdSearchText('');
        setIdSearchError(null);
    };

    return (
        <Box>
            {selectedLinkName ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', flexWrap: 'wrap'}}>
                    <Typography variant="body2" sx={{flex: 1, minWidth: 0}}>{selectedLinkName}</Typography>
                    <Button size="small" color="error" variant="outlined" onClick={handleClear}>Clear</Button>
                </Box>
            ) : (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    <TextField
                        label="Search match by name"
                        placeholder="Type at least 2 characters…"
                        value={searchText}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onChange={handleInputChange}
                        fullWidth
                        variant="filled"
                    />
                    <TextField
                        label="Or enter match ID"
                        placeholder="Match ID…"
                        value={idSearchText}
                        onChange={e => { setIdSearchText(e.target.value); setIdSearchError(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') handleSearchById(); }}
                        fullWidth
                        variant="filled"
                        type="number"
                        error={!!idSearchError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearchById} disabled={idSearching || !idSearchText} edge="end" aria-label="Search by id">
                                        {idSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {idSearchError && <Alert severity="error" sx={{py: 0}}>{idSearchError}</Alert>}
                </Box>
            )}
            <Popper open={menuOpen && matchList.length > 0} anchorEl={anchorEl} placement="bottom-start" sx={{zIndex: 1300, minWidth: anchorEl?.clientWidth, maxWidth: 'min(600px, 100vw)'}}>
                <Box sx={{backgroundColor: 'background.paper', boxShadow: 3, maxHeight: 400, overflow: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider'}}>
                    {matchList.map((option) => (
                        <MenuItem
                            key={option.betupId}
                            onClick={() => handleMenuItemClick(option)}
                            sx={{whiteSpace: 'normal', py: 1}}
                        >
                            <ListItemText
                                primary={`[${option.betupId}] ${option.homeTeam?.name} vs ${option.awayTeam?.name} — ${formatMatchDate(option.date)}`}
                                secondary={option.sport?.name ?? 'Unknown sport'}
                                primaryTypographyProps={{variant: 'body2', fontWeight: 500}}
                                secondaryTypographyProps={{variant: 'caption', color: 'text.secondary'}}
                            />
                        </MenuItem>
                    ))}
                </Box>
            </Popper>
        </Box>
    );
}

export function LeagueSearchSelect({selectedLinkName, onChoose, onClear}: SearchSelectProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [leagueList, setLeagueList] = useState<LeagueInfoDto[]>([]);
    const [searchText, setSearchText] = useState('');
    const [idSearchText, setIdSearchText] = useState('');
    const [idSearchError, setIdSearchError] = useState<string | null>(null);
    const [idSearching, setIdSearching] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setAnchorEl(event.currentTarget);
        if (!selectedLinkName) {
            setMenuOpen(true);
        }
    };

    const handleInputBlur = () => {
        setTimeout(() => setMenuOpen(false), 200);
    };

    const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchText(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            if (value.length < 2) {
                setLeagueList([]);
                return;
            }
            try {
                const response = await getSearchLeagues(value);
                setLeagueList(response.items.slice(0, 20));
                setMenuOpen(true);
            } catch (error) {
                console.error('Error fetching leagues:', error);
            }
        }, 300);
    };

    const handleMenuItemClick = (league: LeagueInfoDto) => {
        const linkName = `[${league.id}] ${league.name}${league.sport ? ' - ' + league.sport.name : ''}`;
        onChoose(league.id, linkName);
        setSearchText('');
        setMenuOpen(false);
        setIdSearchError(null);
    };

    const handleSearchById = async () => {
        const id = parseInt(idSearchText, 10);
        if (isNaN(id)) {
            setIdSearchError('Please enter a valid numeric League ID');
            return;
        }
        setIdSearching(true);
        setIdSearchError(null);
        try {
            const league = await getLeagueById(id);
            if (league) {
                const linkName = `[${league.id}] ${league.name}${league.sport ? ' - ' + league.sport.name : ''}`;
                onChoose(league.id, linkName);
                setIdSearchText('');
            } else {
                setIdSearchError(`League was not found for ID: ${id}`);
            }
        } catch (error) {
            setIdSearchError(`League was not found for ID: ${id}`);
        } finally {
            setIdSearching(false);
        }
    };

    const handleClear = () => {
        onClear();
        setSearchText('');
        setLeagueList([]);
        setIdSearchText('');
        setIdSearchError(null);
    };

    const getLeagueDisplayName = (league: LeagueInfoDto) => {
        return `[${league.id}] ${league.name}${league.sport ? ' - ' + league.sport.name : ''}`;
    };

    return (
        <Box>
            {selectedLinkName ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', flexWrap: 'wrap'}}>
                    <Typography variant="body2" sx={{flex: 1, minWidth: 0}}>{selectedLinkName}</Typography>
                    <Button size="small" color="error" variant="outlined" onClick={handleClear}>Clear</Button>
                </Box>
            ) : (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    <TextField
                        label="Search league by name"
                        placeholder="Type at least 2 characters…"
                        value={searchText}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onChange={handleInputChange}
                        fullWidth
                        variant="filled"
                    />
                    <TextField
                        label="Or enter league ID"
                        placeholder="League ID…"
                        value={idSearchText}
                        onChange={e => { setIdSearchText(e.target.value); setIdSearchError(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') handleSearchById(); }}
                        fullWidth
                        variant="filled"
                        type="number"
                        error={!!idSearchError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearchById} disabled={idSearching || !idSearchText} edge="end" aria-label="Search by id">
                                        {idSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {idSearchError && <Alert severity="error" sx={{py: 0}}>{idSearchError}</Alert>}
                </Box>
            )}
            <Popper open={menuOpen && leagueList.length > 0} anchorEl={anchorEl} placement="bottom-start" sx={{zIndex: 1300, width: anchorEl?.clientWidth, maxWidth: '100vw'}}>
                <Box sx={{backgroundColor: 'background.paper', boxShadow: 3, maxHeight: 300, overflow: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider'}}>
                    {leagueList.map((option) => (
                        <MenuItem
                            key={option.id}
                            onClick={() => handleMenuItemClick(option)}
                        >
                            {getLeagueDisplayName(option)}
                        </MenuItem>
                    ))}
                </Box>
            </Popper>
        </Box>
    );
}
